import { z } from 'zod';

// ─── Enums ────────────────────────────────────────────────────────────────────

export const PageTypeEnum = z.enum(['third_party', 'custom_dashboard', 'dashboard', 'built_in_code']);
export type PageType = z.infer<typeof PageTypeEnum>;

export const BuiltInCodePageEnum = z.enum(['frm-account-insights', 'frm-territory-insights']);
export type BuiltInCodePage = z.infer<typeof BuiltInCodePageEnum>;

// ─── Base Models ──────────────────────────────────────────────────────────────

/**
 * PageVersionSchema represents a saved snapshot of a dashboard config.
 * The `config` field holds a full DashboardConfig JSON object — validated
 * against DashboardConfigSchema (@trinity/chat) in the service layer.
 */
export const PageVersionSchema = z.object({
  id: z.number().int(),
  pageId: z.number().int(),
  versionNumber: z.number().int().min(1),
  /** Full DashboardConfig JSON snapshot */
  config: z.record(z.string(), z.unknown()),
  createdBy: z.number().nullable(),
  createdOn: z.coerce.date(),
  description: z.string().nullable(),
});
export type PageVersion = z.infer<typeof PageVersionSchema>;

export const PageSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  type: PageTypeEnum,
  dataConnector: z.string().nullable(),
  templateId: z.number().nullable(),
  currentVersionId: z.number().nullable(),
  /** For third_party and built_in_code pages: provider-specific page metadata. */
  providerData: z.record(z.string(), z.unknown()),
  isTemplate: z.boolean(),
  createdBy: z.number().nullable(),
  createdOn: z.coerce.date(),
  updatedBy: z.number(),
  updatedOn: z.coerce.date(),
});
export type Page = z.infer<typeof PageSchema>;

/** Page with the current version config inlined */
export const PageDetailSchema = PageSchema.extend({
  currentVersion: PageVersionSchema.nullable(),
});
export type PageDetail = z.infer<typeof PageDetailSchema>;

// ─── List Response ────────────────────────────────────────────────────────────

export const GetPagesResponseSchema = z.object({
  pages: z.array(PageDetailSchema),
});
export type GetPagesResponse = z.infer<typeof GetPagesResponseSchema>;

export const GetPageVersionsResponseSchema = z.object({
  versions: z.array(PageVersionSchema),
});
export type GetPageVersionsResponse = z.infer<typeof GetPageVersionsResponseSchema>;

// ─── Third-party provider data ────────────────────────────────────────────────

/** Plain iframe rendered with Figma-specific sizing (padding, calc height). */
export const FigmaProviderDataSchema = z.object({
  provider: z.literal('figma'),
  url: z.url(),
});
export type FigmaProviderData = z.infer<typeof FigmaProviderDataSchema>;

/**
 * Iframe cropped with absolute positioning to hide the external site's own
 * navigation bars. `topPadding` (px) fine-tunes the top crop offset.
 */
export const DmiProviderDataSchema = z.object({
  provider: z.literal('dmi'),
  url: z.url(),
  topPadding: z.number().int().default(20),
});
export type DmiProviderData = z.infer<typeof DmiProviderDataSchema>;

/** Rendered via the Tableau JS embedding API (`<tableau-viz>`). */
export const TableauProviderDataSchema = z.object({
  provider: z.literal('tableau'),
  url: z.url(),
});
export type TableauProviderData = z.infer<typeof TableauProviderDataSchema>;

export const ThirdPartyProviderDataSchema = z.discriminatedUnion('provider', [
  FigmaProviderDataSchema,
  DmiProviderDataSchema,
  TableauProviderDataSchema,
]);
export type ThirdPartyProviderData = z.infer<typeof ThirdPartyProviderDataSchema>;

export const BuiltInCodeProviderDataSchema = z.object({
  codePage: BuiltInCodePageEnum,
});
export type BuiltInCodeProviderData = z.infer<typeof BuiltInCodeProviderDataSchema>;
